import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import Navbar from '../components/common/Navbar';
import LoadingSpinner from '../components/common/LoadingSpinner';
import PlanUpsellWall from '../components/common/PlanUpsellWall';
import KpiMonthHeader from '../components/kpi/KpiMonthHeader';
import KpiOverview from '../components/kpi/KpiOverview';
import KpiRoleView from '../components/kpi/KpiRoleView';
import KpiGoalsEditor from '../components/kpi/KpiGoalsEditor';
import KpiAnalytics from '../components/kpi/KpiAnalytics';
import KpiAccounting from '../components/kpi/KpiAccounting';
import KpiHelp from '../components/kpi/KpiHelp';
import { getKPIMonth } from '../services/api';
import '../components/kpi/KpiPage.css';

/* Иконки рисуем штрихом вместо эмодзи — см. Navbar.jsx, откуда взят паттерн. */
const ChartIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 22H21" /><path d="M5 19V11" /><path d="M12 19V5" /><path d="M19 19V9" />
  </svg>
);

const TABS = [
  { key: 'overview', label: 'Обзор' },
  { key: 'roles', label: 'Должности' },
  { key: 'goals', label: 'Цели и шкалы' },
  { key: 'analytics', label: 'Аналитика' },
  { key: 'accounting', label: 'Бухгалтеру' },
];

const currentPeriod = () => {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
};

/* Страница KPI. Все расчёты — на сервере (kpi.py): здесь только показ и
   ввод. Месяц — главная единица: у него свои цели, свой факт и свой
   статус «черновик / закрыт». */
const KPIPage = () => {
  const { user, isAuthenticated, organization, logout } = useAuth();
  const isGated = organization?.status === 'active' && organization?.plan
    && organization.plan.has_kpi === false;
  const [period, setPeriod] = useState(currentPeriod);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('overview');
  const [role, setRole] = useState('waiters');
  const [helpOpen, setHelpOpen] = useState(false);
  const [toast, setToast] = useState(null);

  const notify = useCallback((kind, text) => {
    setToast({ kind, text });
    setTimeout(() => setToast(null), 3600);
  }, []);

  const load = useCallback(async () => {
    if (isGated) { setLoading(false); return; }
    setError(null);
    try {
      setData(await getKPIMonth(period.year, period.month));
    } catch (e) {
      setError(e.response?.data?.error || 'Не удалось загрузить KPI');
    } finally {
      setLoading(false);
    }
  }, [isGated, period]);

  useEffect(() => { load(); }, [load]);

  /* Любая правка возвращает свежий месяц целиком — так баллы и подсказки
     на экране всегда совпадают с тем, что посчитал сервер. */
  const run = useCallback(async (fn, okText) => {
    try {
      const next = await fn();
      if (next?.employees) setData(next);
      if (okText) notify('ok', okText);
      return next;
    } catch (e) {
      notify('err', e.response?.data?.error || 'Не получилось сохранить');
      throw e;
    }
  }, [notify]);

  const openRole = (key) => { setRole(key); setTab('roles'); };

  if (!isAuthenticated) return null;

  const closed = data?.status === 'closed';

  return (
    <div className="page-container kpi2-page">
      <Navbar user={user} isAdmin={user?.role === 'admin'} onLogout={logout} />

      {loading ? (
        <LoadingSpinner />
      ) : isGated ? (
        <PlanUpsellWall
          title="KPI-аналитика"
          icon={<ChartIcon />}
          bullets={['Цели и премии по должностям на каждый месяц', 'Факт из iiko одной кнопкой', 'Ведомость премий для бухгалтера']}
        />
      ) : (
        <div className="kpi2">
          <KpiMonthHeader
            period={period}
            onPeriod={(p) => { setPeriod(p); setLoading(true); }}
            data={data}
            isAdmin={user?.role === 'admin'}
            onData={setData}
            run={run}
            notify={notify}
            onGoTab={setTab}
            onHelp={() => setHelpOpen(true)}
          />

          {error && <div className="ui-inline-error">{error}</div>}

          <div className="ui-tabs kpi2-tabs" role="tablist">
            {TABS.map(t => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                className={`ui-tab ${tab === t.key ? 'active' : ''}`}
                onClick={() => setTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>

          {data && tab === 'overview' && <KpiOverview data={data} onOpenRole={openRole} />}
          {data && tab === 'roles' && (
            <KpiRoleView data={data} role={role} onRole={setRole} period={period}
              closed={closed} run={run} reload={load} notify={notify} />
          )}
          {data && tab === 'goals' && (
            // key: черновик целей принадлежит должности и месяцу — при смене
            // любого из них редактор начинается заново, а не с чужих полей.
            <KpiGoalsEditor key={`${role}-${period.year}-${period.month}`} data={data} role={role}
              onRole={setRole} period={period}
              closed={closed} run={run} />
          )}
          {data && tab === 'analytics' && <KpiAnalytics data={data} />}
          {data && tab === 'accounting' && (
            <KpiAccounting data={data} period={period} notify={notify} onGoTab={setTab} onReload={load} />
          )}
        </div>
      )}

      {helpOpen && <KpiHelp onClose={() => setHelpOpen(false)} />}

      {toast && (
        <div className={`ui-toast ${toast.kind}`} role="status">{toast.text}</div>
      )}
    </div>
  );
};

export default KPIPage;
